// @ts-nocheck
import {Crown} from "@phosphor-icons/react";
import PropTypes from "prop-types";
import {useEffect, useRef, useState} from "react";
import {useMiniGames} from "../../../../../store/useMiniGames";
import {PromoBanners} from "../../../../public/Home/components/PromoBanners";
import HeadBand from "../Headband";
import CatalogCarousels from "./components/catalog/CatalogCarousels";
import CatalogGrid from "./components/catalog/CatalogGrid";
import CatalogHeader from "./components/catalog/CatalogHeader";
import CatalogToolbar from "./components/catalog/CatalogToolbar";
import useGamesCatalog from "./components/catalog/useGamesCatalog";

const chyronCls =
  "w-full flex items-center gap-0 overflow-hidden select-none" ;

const List = ({provider = "", category = ""}) => {
  const getMiniGamesList = useMiniGames(state => state.getMiniGames);
  const resetFilters = useMiniGames(state => state.resetFilters);
  const allGames = useMiniGames(state => state.list) ?? [];
  const loading = useMiniGames(state => state.loading);
  const error = useMiniGames(state => state.error);
  const providerOptions = useMiniGames(state => state.providerOptions) ?? [];
  const classOptions = useMiniGames(state => state.classOptions) ?? [];
  const favoriteGames = useMiniGames(state => state.favoriteGames) ?? [];

  const gridSectionRef = useRef(null);
  const handleScrollToResults = () => {
    gridSectionRef.current?.scrollIntoView({behavior: "smooth", block: "start"});
  };

  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, {passive: true});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const {
    search,
    setSearch,
    provider: selectedProvider,
    setProvider,
    category: selectedCategory,
    setCategory,
    sort,
    setSort,
    displayedGames,
    totalCount,
    showingCount,
    hasMore,
    loadMore,
    isLoadingMore,
    clearFilters,
    hasActiveFilters,
  } = useGamesCatalog(allGames);

  useEffect(() => {
    getMiniGamesList(provider, category);
    return () => {
      resetFilters();
    };
  }, [provider, category]);

  const handleRetry = () => getMiniGamesList(provider, category);

  return (
    <div className="relative w-full isolate">
      <PromoBanners />
      {/* Decorative ambient orbs — same visual language as home page sections */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 -z-10 overflow-hidden"
      >
        {/* Gold orb — top left */}
        <div
          style={{
            position: "absolute",
            top: "3%",
            left: "-8%",
            width: "600px",
            height: "600px",
            background: "radial-gradient(closest-side, rgba(253,238,51,0.09) 0%, transparent 70%)",
            filter: "blur(48px)",
          }}
        />
        {/* Magenta orb — top right */}
        <div
          style={{
            position: "absolute",
            top: "5%",
            right: "-10%",
            width: "700px",
            height: "700px",
            background: "radial-gradient(closest-side, rgba(215,50,168,0.12) 0%, transparent 70%)",
            filter: "blur(52px)",
          }}
        />
        {/* Orange orb — mid left */}
        <div
          style={{
            position: "absolute",
            top: "40%",
            left: "-5%",
            width: "480px",
            height: "480px",
            background: "radial-gradient(closest-side, rgba(250,84,0,0.08) 0%, transparent 70%)",
            filter: "blur(44px)",
          }}
        />
        {/* Pink/rose orb — bottom right (ties in with toolbar) */}
        <div
          style={{
            position: "absolute",
            bottom: "5%",
            right: "5%",
            width: "520px",
            height: "520px",
            background: "radial-gradient(closest-side, rgba(233,30,140,0.07) 0%, transparent 70%)",
            filter: "blur(40px)",
          }}
        />
      </div>
      {/* Chyron — news ticker style */}
      <div className={chyronCls}>
        {/* Label badge */}
        <div
          className="
            shrink-0 flex items-center gap-1.5 px-3 h-9
            bg-[var(--color-primary)] text-[var(--color-text-inverse)]
            font-[var(--font-family)] font-extrabold text-[11px] uppercase tracking-widest
            whitespace-nowrap
          "
        >
          <Crown size={11} weight="fill" aria-hidden="true" />
          EN VIVO
        </div>
        {/* Separator triangle */}
        <div
          style={{
            width: 0,
            height: 0,
            borderTop: "18px solid transparent",
            borderBottom: "18px solid transparent",
            borderLeft: "14px solid var(--color-primary)",
            flexShrink: 0,
          }}
        />
        {/* Message */}
        <div
          className="
            flex-1 flex items-center gap-2 px-3 h-9
            bg-[rgba(255,255,255,0.04)] border-y border-[rgba(255,255,255,0.06)]
          "
        >
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-secondary)] whitespace-nowrap">
            Están coronando
          </span>
          <span className="w-px h-3 bg-[var(--color-border)] shrink-0" />
          <span className="text-[11px] font-normal tracking-wide text-[var(--color-text-tertiary)] whitespace-nowrap">
            Los jugadores con mayores premios en tiempo real
          </span>
        </div>
      </div>

      <HeadBand />

      {/* Toolbar — sticky, always visible while scrolling */}
      <div
        className={
          "w-full sticky top-0 min-[800px]:top-16 min-[1280px]:top-[5.25rem] z-[var(--z-sticky)] " +
          "px-4 min-[768px]:px-8 min-[1200px]:px-[130px] pt-3 pb-2 " +
          "transition-all duration-300 " +
          (scrolled
            ? "bg-[rgba(10,10,18,0.55)] backdrop-blur-xl backdrop-saturate-150 " +
              "border-b border-[#e91e8c33] " +
              "shadow-[0_8px_32px_-8px_rgba(0,0,0,0.7),0_0_0_1px_#e91e8c18,inset_0_1px_0_#ffffff0a]"
            : "bg-[var(--color-bg)] border-b border-[var(--color-border-subtle)] shadow-[0_4px_24px_-8px_rgba(0,0,0,0.5)]")
        }
      >
        <CatalogToolbar
          search={search}
          onSearchChange={setSearch}
          provider={selectedProvider}
          onProviderChange={setProvider}
          category={selectedCategory}
          onCategoryChange={setCategory}
          sort={sort}
          onSortChange={setSort}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearFilters}
          providerOptions={providerOptions}
          categoryOptions={classOptions}
          onScrollToResults={handleScrollToResults}
        />
      </div>

      <CatalogCarousels />

      {/* Grid — todos los juegos, after carousels */}
      <section
        ref={gridSectionRef}
        aria-label="Todos los juegos"
        className="w-full px-4 min-[768px]:px-8 min-[1200px]:px-[130px] pt-4 pb-16 scroll-mt-36 min-[1280px]:scroll-mt-44"
      >
        <CatalogHeader
          totalCount={totalCount}
          showingCount={showingCount}
        />
        <CatalogGrid
          games={displayedGames}
          loading={loading}
          error={Boolean(error)}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          favoriteGames={favoriteGames}
          showingCount={showingCount}
          onLoadMore={loadMore}
          onRetry={handleRetry}
        />
      </section>
    </div>
  );
};

List.propTypes = {
  provider: PropTypes.string,
  category: PropTypes.string,
};

export default List;
